/**
 * RepoDetail — Repo_Detail_View for a single Repo_Result (Req 8, 9, 10.2).
 *
 * Fetches the *original* `<repo>.json` (the byte-identical `RepoScanResult` the
 * scanner wrote) on demand using `RepoSummary.detailKey`, maps it through the
 * pure `buildDetailViewModel`, and renders every section: the seven top-level
 * fields (Req 8.1), the GitHub metadata fields with a metadata-unavailable
 * placeholder when absent (Req 8.2, 10.2), and the documentation, ownership,
 * cicd, manifests, infra, api (with each route's fields), runtime, domain,
 * errors, and truncated_files sections (Req 8.4-8.10). Null/empty values render
 * as "Not provided" / "No entries" placeholders (Req 8.3, 8.11).
 *
 * Secret-safety (Req 9): the runtime section renders `env_var_names` and
 * `secret_like_env_vars` as *names only*; every secret-like entry carries a
 * visible "secret-like" marker, and empty collections show a "no entries"
 * indicator (Req 9.1, 9.2, 9.4). Any out-of-schema key the source carried is
 * surfaced by name only through the withheld indicator (Req 9.5).
 *
 * External links — `metadata.html_url` and the top-level `repo_url` — open in a
 * new browser context via `target="_blank" rel="noopener noreferrer"` so the
 * detail view is never replaced (Req 8.15).
 *
 * The component owns only fetch lifecycle (loading / error / loaded). A failed
 * fetch surfaces an "unavailable" indication and never crashes; the App layer
 * is responsible for the deep-link redirect to the list (Req 8.14). Both the
 * fetch implementation and the URL builder are injectable for testability.
 *
 * Styling (task 5.6): content sits on a white Card with ~8px radius; headings
 * use the documented type scale and the close control follows the secondary
 * button pattern. All visual values come from token-referencing classes in
 * `components.css` (Req 23.2, 24.5); ARIA roles/labels and link behavior are
 * preserved.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { LibraryConfidence, LibraryRepoEntry, RepoScanResult } from "../types";
import {
  buildDetailViewModel,
  type Collection,
  type DetailViewModel,
  type EnvVarEntry,
  type Field,
  type MetadataFields,
} from "../lib/detailViewModel";
import {
  buildClassificationArtifactUrl,
  buildNarrativeArtifactUrl,
  parseClassificationArtifact,
  parseNarrativeArtifact,
  type ClassificationEvidence,
  type RepoClassificationArtifact,
  type NarrativeArtifact,
} from "../lib/libraryArtifacts";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link RepoDetail}. */
export interface RepoDetailProps {
  /** S3 key of the source `<repo>.json` to fetch on demand (Req 8.13). */
  detailKey: string;
  /**
   * Repository name for the heading / unavailable message. Optional; the
   * fetched result's `repo_name` is used once loaded.
   */
  repoName?: string;
  /** Fetch implementation; defaults to the global `fetch`. Injected in tests. */
  fetchFn?: typeof fetch;
  /** Override the detail URL builder; defaults to {@link buildDetailUrl}. */
  buildUrl?: (detailKey: string) => string;
  /**
   * Called when the source object cannot be fetched/parsed (Req 8.14). The App
   * uses this to navigate back to the list; the component itself never crashes
   * and shows an unavailable indication regardless.
   */
  onUnavailable?: (detailKey: string) => void;
  /** Called when the user closes / navigates back from the detail view (Req 8.12). */
  onClose?: () => void;

  // --- Repo Knowledge Library (ADDITIVE — Req 18.4, 18.6) ----------------- //
  // All optional: with none of these the detail view renders EXACTLY as today.

  /**
   * The joined dashboard Library entry for this repo (five classification
   * dimensions + per-decision confidence + activity metrics + short
   * description). `null`/absent when no Library document exists for the run (an
   * older run), in which case the Knowledge Library panels degrade away.
   */
  libraryEntry?: LibraryRepoEntry | null;
  /**
   * The run's date partition (`YYYY-MM-DD`). Used together with `runId` and the
   * repo name to build the per-repo classification/narrative artifact URLs for
   * the Evidence + full narrative panels. Absent => those artifacts are never
   * fetched.
   */
  runDate?: string;
  /** The run's identifier; see {@link RepoDetailProps.runDate}. */
  runId?: string;
  /**
   * Override the classification artifact URL builder; defaults to
   * {@link buildClassificationArtifactUrl}. Injected in tests.
   */
  buildClassificationUrl?: (
    runDate: string,
    runId: string,
    repoName: string,
  ) => string;
  /**
   * Override the narrative artifact URL builder; defaults to
   * {@link buildNarrativeArtifactUrl}. Injected in tests.
   */
  buildNarrativeUrl?: (
    runDate: string,
    runId: string,
    repoName: string,
  ) => string;
}

// ---------------------------------------------------------------------------
// URL building
// ---------------------------------------------------------------------------

/**
 * Build the detail URL from a `detailKey`. The key is the S3 object key the
 * aggregator recorded (e.g. `scans/run-date=.../run-id=.../<repo>.json`). It is
 * normalized to a root-absolute path (leading slash) so it resolves against the
 * deployed origin regardless of the current client route (e.g.
 * `/run/:runDate/:runId/repo/:repoName`) and maps to the CloudFront `scans/*`
 * cache behavior.
 */
export function buildDetailUrl(detailKey: string): string {
  return detailKey.startsWith("/") ? detailKey : `/${detailKey}`;
}

// ---------------------------------------------------------------------------
// Fetch lifecycle
// ---------------------------------------------------------------------------

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; model: DetailViewModel; result: RepoScanResult };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Indication shown while the source object is being fetched (Req 8.1). */
export const LOADING_MESSAGE = "Loading repository details\u2026";

/** Indication shown when the source object cannot be loaded (Req 8.14). */
export const UNAVAILABLE_MESSAGE = "The requested repository is unavailable.";

/**
 * Detail view for a single Repo_Result. Fetches the original `<repo>.json`,
 * builds the detail view-model, and renders every section with placeholders for
 * null/empty values and a secret-safe runtime section.
 */
export default function RepoDetail({
  detailKey,
  repoName,
  fetchFn,
  buildUrl = buildDetailUrl,
  onUnavailable,
  onClose,
  libraryEntry,
  runDate,
  runId,
  buildClassificationUrl = buildClassificationArtifactUrl,
  buildNarrativeUrl = buildNarrativeArtifactUrl,
}: RepoDetailProps) {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  // ADDITIVE (Req 18.4): the per-repo structured Library artifacts, fetched in
  // a SEPARATE effect from the source `<repo>.json`. Both default to null and
  // any fetch/parse failure leaves them null, so the existing detail render is
  // never affected (graceful absence — older runs render exactly as today).
  const [classification, setClassification] =
    useState<RepoClassificationArtifact | null>(null);
  const [narrative, setNarrative] = useState<NarrativeArtifact | null>(null);

  // Keep the latest onUnavailable without re-running the fetch effect.
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: "loading" });

    const doFetch = fetchFn ?? fetch;

    void (async () => {
      try {
        const response = await doFetch(buildUrl(detailKey), {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`status ${response.status}`);
        }
        const body = (await response.json()) as RepoScanResult;
        // buildDetailViewModel is total and never raises, even for malformed
        // input, so a successful fetch always yields a renderable model.
        const model = buildDetailViewModel(body);
        setState({ status: "loaded", model, result: body });
      } catch (caught) {
        // A superseded fetch (newer detailKey) aborts us — drop silently.
        if (controller.signal.aborted) {
          return;
        }
        setState({ status: "error" });
        onUnavailableRef.current?.(detailKey);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [detailKey, fetchFn, buildUrl]);

  // ADDITIVE (Req 18.4, 18.6): fetch the per-repo classification + narrative
  // artifacts ONLY when we have the run coordinates and a repo name to build
  // their keys. This is a SEPARATE effect from the `<repo>.json` fetch above,
  // sharing one AbortController. ANY failure (403/404/network/parse) leaves the
  // respective artifact `null` and NEVER touches `state`, so older runs without
  // Library artifacts render exactly as today.
  useEffect(() => {
    // Reset on dependency change so a prior repo's artifacts never leak.
    setClassification(null);
    setNarrative(null);

    if (!runDate || !runId || !repoName) {
      return;
    }

    const controller = new AbortController();
    const doFetch = fetchFn ?? fetch;

    const loadOne = async <T,>(
      url: string,
      parse: (raw: unknown) => T | null,
      apply: (value: T | null) => void,
    ): Promise<void> => {
      try {
        const response = await doFetch(url, { signal: controller.signal });
        if (!response.ok) {
          return; // absent artifact (e.g. older run) — stay null
        }
        const body = (await response.json()) as unknown;
        if (controller.signal.aborted) {
          return;
        }
        apply(parse(body));
      } catch {
        // Network/parse/abort failure — leave the artifact null (graceful).
      }
    };

    void loadOne(
      buildClassificationUrl(runDate, runId, repoName),
      parseClassificationArtifact,
      setClassification,
    );
    void loadOne(
      buildNarrativeUrl(runDate, runId, repoName),
      parseNarrativeArtifact,
      setNarrative,
    );

    return () => {
      controller.abort();
    };
  }, [runDate, runId, repoName, fetchFn, buildClassificationUrl, buildNarrativeUrl]);

  if (state.status === "loading") {
    return (
      <section aria-label="Repository detail" className="repo-detail">
        <p role="status" className="repo-detail__message">
          {LOADING_MESSAGE}
        </p>
      </section>
    );
  }

  if (state.status === "error") {
    return (
      <section aria-label="Repository detail" className="repo-detail">
        <DetailHeader title={repoName ?? detailKey} onClose={onClose} />
        <p role="alert" className="repo-detail__unavailable">
          {UNAVAILABLE_MESSAGE}
        </p>
      </section>
    );
  }

  const { model } = state;
  const title =
    model.topLevel.repoName.present
      ? model.topLevel.repoName.value
      : (repoName ?? detailKey);

  return (
    <section aria-label="Repository detail" className="repo-detail">
      <DetailHeader title={title} onClose={onClose} />
      <TopLevelSectionView model={model} />
      <MetadataSectionView model={model} />
      <LibraryKnowledgeView
        libraryEntry={libraryEntry ?? null}
        classification={classification}
        narrative={narrative}
      />
      <DocumentationSectionView model={model} />
      <OwnershipSectionView model={model} />
      <CicdSectionView model={model} />
      <ManifestsSectionView model={model} />
      <InfraSectionView model={model} />
      <ApiSectionView model={model} />
      <RuntimeSectionView model={model} />
      <DomainSectionView model={model} />
      <ErrorsSectionView model={model} />
      <TruncatedFilesSectionView model={model} />
      <WithheldView model={model} />
    </section>
  );
}

// ===========================================================================
// Presentational primitives
// ===========================================================================

/** Header with the repository title and an optional close/back control (Req 8.12). */
function DetailHeader({
  title,
  onClose,
}: {
  title: string;
  onClose?: () => void;
}) {
  return (
    <header className="repo-detail__header">
      <h2 className="repo-detail__title">{title}</h2>
      {onClose && (
        <button type="button" onClick={onClose} className="repo-detail__close">
          Back to list
        </button>
      )}
    </header>
  );
}

/** A titled section wrapper. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="repo-detail__section" aria-label={title}>
      <h3 className="repo-detail__section-title">{title}</h3>
      {children}
    </section>
  );
}

/** A muted placeholder span (e.g. "Not provided", "No entries"). */
function PlaceholderText({ text }: { text: string }) {
  return (
    <span className="repo-detail__placeholder" title={text}>
      {text}
    </span>
  );
}

/** A label/value definition row. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="repo-detail__row">
      <span className="repo-detail__label">{label}</span>
      <span className="repo-detail__value">{children}</span>
    </div>
  );
}

/**
 * Render a {@link Field} as a row, using `render` for the present value and the
 * field's placeholder otherwise (Req 8.3).
 */
function FieldRow<T>({
  label,
  field,
  render,
}: {
  label: string;
  field: Field<T>;
  render?: (value: T) => ReactNode;
}) {
  return (
    <Row label={label}>
      {field.present ? (
        render ? (
          render(field.value)
        ) : (
          <span>{String(field.value)}</span>
        )
      ) : (
        <PlaceholderText text={field.placeholder} />
      )}
    </Row>
  );
}

/** Render a boolean {@link Field} as Yes/No (placeholder when absent). */
function BoolFieldRow({ label, field }: { label: string; field: Field<boolean> }) {
  return <FieldRow label={label} field={field} render={(v) => (v ? "Yes" : "No")} />;
}

/**
 * Render an external link {@link Field} as an anchor opening in a new browser
 * context (Req 8.15). Absent values fall back to the placeholder.
 */
function LinkFieldRow({ label, field }: { label: string; field: Field<string> }) {
  return (
    <FieldRow
      label={label}
      field={field}
      render={(href) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="repo-detail__link"
        >
          {href}
        </a>
      )}
    />
  );
}

/**
 * Render a {@link Collection} of strings as a row: a bulleted list of entries,
 * or the "no entries" placeholder when empty (Req 8.11).
 */
function StringCollectionRow({
  label,
  collection,
}: {
  label: string;
  collection: Collection<string>;
}) {
  return (
    <Row label={label}>
      {collection.empty ? (
        <PlaceholderText text={collection.placeholder} />
      ) : (
        <ul className="repo-detail__list">
          {collection.entries.map((entry, index) => (
            <li key={`${entry}\u0000${index}`} className="repo-detail__list-item">
              {entry}
            </li>
          ))}
        </ul>
      )}
    </Row>
  );
}

/**
 * Render a {@link Collection} of numbers as a row (e.g. exposed ports), or the
 * "no entries" placeholder when empty (Req 8.11).
 */
function NumberCollectionRow({
  label,
  collection,
}: {
  label: string;
  collection: Collection<number>;
}) {
  return (
    <Row label={label}>
      {collection.empty ? (
        <PlaceholderText text={collection.placeholder} />
      ) : (
        <ul className="repo-detail__list">
          {collection.entries.map((entry, index) => (
            <li key={`${entry}\u0000${index}`} className="repo-detail__list-item">
              {entry}
            </li>
          ))}
        </ul>
      )}
    </Row>
  );
}

/**
 * Render an environment-variable {@link Collection} — names only — marking
 * secret-like entries with a visible indicator (Req 9.1, 9.2); empty shows the
 * "no entries" indicator (Req 9.4).
 */
function EnvVarCollectionRow({
  label,
  collection,
}: {
  label: string;
  collection: Collection<EnvVarEntry>;
}) {
  return (
    <Row label={label}>
      {collection.empty ? (
        <PlaceholderText text={collection.placeholder} />
      ) : (
        <ul className="repo-detail__list">
          {collection.entries.map((entry, index) => (
            <li key={`${entry.name}\u0000${index}`} className="repo-detail__list-item">
              <span>{entry.name}</span>
              {entry.secretLike && entry.marker && (
                <span className="repo-detail__secret-marker" title={entry.marker}>
                  {entry.marker}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Row>
  );
}

// ===========================================================================
// Section views
// ===========================================================================

/** The seven always-present top-level fields (Req 8.1). */
function TopLevelSectionView({ model }: { model: DetailViewModel }) {
  const t = model.topLevel;
  return (
    <Section title="Overview">
      <FieldRow label="Repository name" field={t.repoName} />
      <LinkFieldRow label="Repository URL" field={t.repoUrl} />
      <FieldRow label="Status" field={t.status} />
      <FieldRow label="Scanner version" field={t.scannerVersion} />
      <FieldRow label="Scan ID" field={t.scanId} />
      <FieldRow label="Scan started at" field={t.scanStartedAt} />
      <FieldRow label="Scan completed at" field={t.scanCompletedAt} />
    </Section>
  );
}

/**
 * The GitHub metadata section (Req 8.2). When metadata is absent a single
 * "GitHub metadata unavailable" placeholder is shown (Req 10.2).
 */
function MetadataSectionView({ model }: { model: DetailViewModel }) {
  if (!model.metadata.available) {
    return (
      <Section title="GitHub metadata">
        <p role="status" className="repo-detail__metadata-unavailable">
          {model.metadata.placeholder}
        </p>
      </Section>
    );
  }
  const m: MetadataFields = model.metadata.fields;
  return (
    <Section title="GitHub metadata">
      <FieldRow label="Name" field={m.name} />
      <FieldRow label="Full name" field={m.fullName} />
      <LinkFieldRow label="HTML URL" field={m.htmlUrl} />
      <FieldRow label="Clone URL" field={m.cloneUrl} />
      <FieldRow label="Default branch" field={m.defaultBranch} />
      <BoolFieldRow label="Archived" field={m.archived} />
      <FieldRow label="Visibility" field={m.visibility} />
      <BoolFieldRow label="Private" field={m.private} />
      <FieldRow label="Created at" field={m.createdAt} />
      <FieldRow label="Updated at" field={m.updatedAt} />
      <FieldRow label="Pushed at" field={m.pushedAt} />
      <FieldRow label="Last commit date" field={m.lastCommitDate} />
      <FieldRow label="Primary language" field={m.primaryLanguage} />
      <Row label="Languages">
        {m.languages.empty ? (
          <PlaceholderText text={m.languages.placeholder} />
        ) : (
          <ul className="repo-detail__list">
            {m.languages.entries.map((lang, index) => (
              <li key={`${lang.name}\u0000${index}`} className="repo-detail__list-item">
                <span>{lang.name}</span>
                <span className="repo-detail__muted">
                  {": "}
                  {lang.bytes.present ? (
                    `${lang.bytes.value} bytes`
                  ) : (
                    <PlaceholderText text={lang.bytes.placeholder} />
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Row>
      <StringCollectionRow label="Topics" collection={m.topics} />
      <FieldRow label="Description" field={m.description} />
      <FieldRow label="Size" field={m.size} />
    </Section>
  );
}

/** The `documentation` section (Req 8.4). */
function DocumentationSectionView({ model }: { model: DetailViewModel }) {
  const d = model.documentation;
  return (
    <Section title="Documentation">
      <BoolFieldRow label="README found" field={d.readmeFound} />
      <FieldRow label="README path" field={d.readmePath} />
      <FieldRow label="README excerpt" field={d.readmeExcerpt} />
      <FieldRow label="README quality guess" field={d.readmeQualityGuess} />
      <BoolFieldRow label="Docs directory found" field={d.docsDirFound} />
      <BoolFieldRow label="Runbook found" field={d.runbookFound} />
      <BoolFieldRow label="ADR found" field={d.adrFound} />
      <BoolFieldRow label="OpenAPI found" field={d.openapiFound} />
      <BoolFieldRow label="Swagger found" field={d.swaggerFound} />
      <BoolFieldRow label="AsyncAPI found" field={d.asyncapiFound} />
      <StringCollectionRow label="Docs paths" collection={d.docsPaths} />
      <StringCollectionRow label="Runbook paths" collection={d.runbookPaths} />
      <StringCollectionRow label="ADR paths" collection={d.adrPaths} />
      <StringCollectionRow label="OpenAPI paths" collection={d.openapiPaths} />
      <StringCollectionRow label="Swagger paths" collection={d.swaggerPaths} />
      <StringCollectionRow label="AsyncAPI paths" collection={d.asyncapiPaths} />
    </Section>
  );
}

/** The `ownership` section (Req 8.5). */
function OwnershipSectionView({ model }: { model: DetailViewModel }) {
  const o = model.ownership;
  return (
    <Section title="Ownership">
      <FieldRow label="Owning team guess" field={o.owningTeamGuess} />
      <FieldRow label="Owner confidence" field={o.ownerConfidence} />
      <StringCollectionRow label="CODEOWNERS paths" collection={o.codeownersPaths} />
      <StringCollectionRow label="catalog-info paths" collection={o.catalogInfoPaths} />
      <StringCollectionRow label="README owner hints" collection={o.readmeOwnerHints} />
      <StringCollectionRow label="Evidence paths" collection={o.evidencePaths} />
    </Section>
  );
}

/** The `cicd` section (Req 8.6). */
function CicdSectionView({ model }: { model: DetailViewModel }) {
  const c = model.cicd;
  return (
    <Section title="CI/CD">
      <BoolFieldRow label="GitHub Actions found" field={c.githubActionsFound} />
      <BoolFieldRow label="Deploy workflow guess" field={c.deployWorkflowGuess} />
      <StringCollectionRow label="Workflow paths" collection={c.workflowPaths} />
      <StringCollectionRow label="Buildspec paths" collection={c.buildspecPaths} />
      <StringCollectionRow label="Jenkinsfile paths" collection={c.jenkinsfilePaths} />
      <StringCollectionRow label="Evidence paths" collection={c.evidencePaths} />
    </Section>
  );
}

/** The `manifests` section (Req 8.6). */
function ManifestsSectionView({ model }: { model: DetailViewModel }) {
  const m = model.manifests;
  return (
    <Section title="Manifests">
      <BoolFieldRow label="package.json found" field={m.packageJsonFound} />
      <BoolFieldRow label="pom.xml found" field={m.pomXmlFound} />
      <BoolFieldRow label="build.gradle found" field={m.buildGradleFound} />
      <BoolFieldRow label="requirements.txt found" field={m.requirementsTxtFound} />
      <BoolFieldRow label="pyproject.toml found" field={m.pyprojectTomlFound} />
      <BoolFieldRow label="go.mod found" field={m.goModFound} />
      <BoolFieldRow label="Cargo.toml found" field={m.cargoTomlFound} />
      <BoolFieldRow label="Gemfile found" field={m.gemfileFound} />
      <BoolFieldRow label="composer.json found" field={m.composerJsonFound} />
      <BoolFieldRow label=".csproj found" field={m.csprojFound} />
      <BoolFieldRow label="Solution found" field={m.solutionFound} />
      <StringCollectionRow label="Manifest paths" collection={m.manifestPaths} />
      <StringCollectionRow label="Dependencies detected" collection={m.dependenciesDetected} />
      <StringCollectionRow
        label="Detected package managers"
        collection={m.detectedPackageManagers}
      />
      <BoolFieldRow label="Parse incomplete" field={m.parseIncomplete} />
      <StringCollectionRow label="Parse warnings" collection={m.parseWarnings} />
    </Section>
  );
}

/** The `infra` section (Req 8.6). */
function InfraSectionView({ model }: { model: DetailViewModel }) {
  const i = model.infra;
  return (
    <Section title="Infrastructure">
      <BoolFieldRow label="Dockerfile found" field={i.dockerfileFound} />
      <BoolFieldRow label="docker-compose found" field={i.dockerComposeFound} />
      <BoolFieldRow label="Kubernetes found" field={i.kubernetesFound} />
      <BoolFieldRow label="Helm found" field={i.helmFound} />
      <BoolFieldRow label="Terraform found" field={i.terraformFound} />
      <BoolFieldRow label="Serverless found" field={i.serverlessFound} />
      <BoolFieldRow label="CloudFormation found" field={i.cloudformationFound} />
      <BoolFieldRow label="SAM found" field={i.samFound} />
      <StringCollectionRow label="Dockerfile paths" collection={i.dockerfilePaths} />
      <StringCollectionRow label="docker-compose paths" collection={i.dockerComposePaths} />
      <StringCollectionRow label="Kubernetes paths" collection={i.kubernetesPaths} />
      <StringCollectionRow label="Helm paths" collection={i.helmPaths} />
      <StringCollectionRow label="Terraform paths" collection={i.terraformPaths} />
      <StringCollectionRow label="Serverless paths" collection={i.serverlessPaths} />
      <StringCollectionRow label="CloudFormation paths" collection={i.cloudformationPaths} />
      <StringCollectionRow label="SAM paths" collection={i.samPaths} />
      <StringCollectionRow label="Parse warnings" collection={i.parseWarnings} />
    </Section>
  );
}

/** The `api` section including each detected route (Req 8.7). */
function ApiSectionView({ model }: { model: DetailViewModel }) {
  const a = model.api;
  return (
    <Section title="API">
      <StringCollectionRow label="OpenAPI paths" collection={a.openapiPaths} />
      <StringCollectionRow label="Swagger paths" collection={a.swaggerPaths} />
      <StringCollectionRow label="AsyncAPI paths" collection={a.asyncapiPaths} />
      <StringCollectionRow label="Proto paths" collection={a.protoPaths} />
      <Row label="Routes">
        {a.routes.empty ? (
          <PlaceholderText text={a.routes.placeholder} />
        ) : (
          <ul className="repo-detail__list">
            {a.routes.entries.map((route, index) => (
              <li key={index} className="repo-detail__nested-item">
                <FieldRow label="Source path" field={route.sourcePath} />
                <FieldRow label="Method" field={route.method} />
                <FieldRow label="Path" field={route.path} />
                <FieldRow label="Framework" field={route.framework} />
                <FieldRow label="Snippet" field={route.snippet} />
              </li>
            ))}
          </ul>
        )}
      </Row>
      <StringCollectionRow label="Parse warnings" collection={a.parseWarnings} />
    </Section>
  );
}

/**
 * The `runtime` section — secret-safe (Req 8.8, 9). Environment variable names
 * are rendered as names only, with secret-like entries marked.
 */
function RuntimeSectionView({ model }: { model: DetailViewModel }) {
  const r = model.runtime;
  return (
    <Section title="Runtime">
      <StringCollectionRow label="Docker base images" collection={r.dockerBaseImages} />
      <NumberCollectionRow label="Exposed ports" collection={r.exposedPorts} />
      <EnvVarCollectionRow label="Environment variable names" collection={r.envVarNames} />
      <EnvVarCollectionRow
        label="Secret-like environment variables"
        collection={r.secretLikeEnvVars}
      />
      <BoolFieldRow
        label="Cron or schedule detected"
        field={r.cronOrScheduleDetected}
      />
      <StringCollectionRow label="Parse warnings" collection={r.parseWarnings} />
    </Section>
  );
}

/** The `domain` section (Req 8.9). */
function DomainSectionView({ model }: { model: DetailViewModel }) {
  const d = model.domain;
  return (
    <Section title="Domain matches">
      <Row label="Matches">
        {d.matches.empty ? (
          <PlaceholderText text={d.matches.placeholder} />
        ) : (
          <ul className="repo-detail__list">
            {d.matches.entries.map((match, index) => (
              <li key={index} className="repo-detail__nested-item">
                <FieldRow label="Group" field={match.group} />
                <FieldRow label="Keyword" field={match.keyword} />
                <FieldRow label="Path" field={match.path} />
              </li>
            ))}
          </ul>
        )}
      </Row>
    </Section>
  );
}

/** The `errors` list (Req 8.10). */
function ErrorsSectionView({ model }: { model: DetailViewModel }) {
  const errors = model.errors;
  return (
    <Section title="Errors">
      {errors.empty ? (
        <PlaceholderText text={errors.placeholder} />
      ) : (
        <ul className="repo-detail__list">
          {errors.entries.map((error, index) => (
            <li key={index} className="repo-detail__nested-item">
              <FieldRow label="Stage" field={error.stage} />
              <FieldRow label="Code" field={error.code} />
              <FieldRow label="Message" field={error.message} />
              <FieldRow label="Path" field={error.path} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** The `truncated_files` list (Req 8.10). */
function TruncatedFilesSectionView({ model }: { model: DetailViewModel }) {
  return (
    <Section title="Truncated files">
      <StringCollectionRow label="Truncated files" collection={model.truncatedFiles} />
    </Section>
  );
}

/** The withheld (out-of-schema) indicator (Req 9.5). */
function WithheldView({ model }: { model: DetailViewModel }) {
  if (!model.withheld.hasWithheld) {
    return null;
  }
  return (
    <Section title="Withheld values">
      <p role="status" className="repo-detail__withheld">
        {model.withheld.indicator}
      </p>
      <StringCollectionRow
        label="Withheld field names"
        collection={{ empty: false, entries: model.withheld.fieldNames }}
      />
    </Section>
  );
}

// ===========================================================================
// Repo Knowledge Library views (ADDITIVE — Req 18.4, 18.6)
// ===========================================================================
//
// These panels overlay the deterministic Library classification, activity
// metrics, and narrative for the repo on top of the existing detail view. They
// are sourced from the joined `LibraryRepoEntry` (dimensions + confidence +
// metrics + short description) and, for Evidence + the full narrative, the
// per-repo structured artifacts fetched separately. Every panel renders ONLY
// when its data is present; when the library entry AND both artifacts are
// absent, `LibraryKnowledgeView` renders nothing and the detail view is
// byte-for-byte today's. All explicit `"unknown"`/`"unavailable"` markers are
// rendered verbatim (Req 18.6).

/** Human labels for the five classification dimensions. */
const DIMENSION_LABELS = {
  repoType: "Repo type",
  lifecycle: "Lifecycle",
  owningTeam: "Owning team",
  application: "Application",
  product: "Product",
} as const;

/** One resolved classification dimension row for the panel. */
interface DimensionRow {
  label: string;
  /** The classified value (e.g. `"service-api"`, `"unknown"`). */
  value: string;
  /** The per-decision confidence, or null when neither source carries it. */
  confidence: LibraryConfidence | null;
  /** The grounding evidence from the classification artifact (may be empty). */
  evidence: ClassificationEvidence[];
}

/**
 * Resolve the five dimension rows, preferring the joined `LibraryRepoEntry` for
 * the value + confidence and falling back to the classification artifact's
 * decisions; evidence always comes from the artifact (absent => empty).
 */
function resolveDimensions(
  entry: LibraryRepoEntry | null,
  classification: RepoClassificationArtifact | null,
): DimensionRow[] {
  return [
    {
      label: DIMENSION_LABELS.repoType,
      value: entry?.repoType ?? classification?.repo_type.repo_type ?? "unknown",
      confidence:
        entry?.repoTypeConfidence ?? classification?.repo_type.confidence ?? null,
      evidence: classification?.repo_type.evidence ?? [],
    },
    {
      label: DIMENSION_LABELS.lifecycle,
      value:
        entry?.lifecycleStatus ??
        classification?.lifecycle.lifecycle_status ??
        "unknown",
      confidence:
        entry?.lifecycleConfidence ?? classification?.lifecycle.confidence ?? null,
      evidence: classification?.lifecycle.evidence ?? [],
    },
    {
      label: DIMENSION_LABELS.owningTeam,
      value: entry?.owningTeam ?? classification?.ownership.owning_team ?? "unknown",
      confidence:
        entry?.ownershipConfidence ?? classification?.ownership.confidence ?? null,
      evidence: classification?.ownership.evidence ?? [],
    },
    {
      label: DIMENSION_LABELS.application,
      value:
        entry?.application ?? classification?.application.application ?? "unknown",
      confidence:
        entry?.applicationConfidence ??
        classification?.application.confidence ??
        null,
      evidence: classification?.application.evidence ?? [],
    },
    {
      label: DIMENSION_LABELS.product,
      value: entry?.product ?? classification?.product.product ?? "unknown",
      confidence:
        entry?.productConfidence ?? classification?.product.confidence ?? null,
      evidence: classification?.product.evidence ?? [],
    },
  ];
}

/**
 * The Repo Knowledge Library group: classification, activity-metrics, and
 * narrative panels (Req 18.4, 18.6). Renders nothing when the library entry and
 * both artifacts are all absent, so the detail view degrades to today's exactly.
 */
function LibraryKnowledgeView({
  libraryEntry,
  classification,
  narrative,
}: {
  libraryEntry: LibraryRepoEntry | null;
  classification: RepoClassificationArtifact | null;
  narrative: NarrativeArtifact | null;
}) {
  if (libraryEntry === null && classification === null && narrative === null) {
    return null;
  }

  return (
    <>
      <ClassificationPanel
        libraryEntry={libraryEntry}
        classification={classification}
      />
      <ActivityMetricsPanel libraryEntry={libraryEntry} />
      <NarrativePanel libraryEntry={libraryEntry} narrative={narrative} />
    </>
  );
}

/** A small confidence badge; renders nothing when confidence is unknown. */
function ConfidenceBadge({ confidence }: { confidence: LibraryConfidence | null }) {
  if (confidence === null) {
    return null;
  }
  return (
    <span className="repo-detail__muted" title={`confidence: ${confidence}`}>
      {" "}
      ({confidence} confidence)
    </span>
  );
}

/**
 * Classification panel: the five dimensions each with its confidence (Req 18.1)
 * and, when the per-repo classification artifact is present, the grounding
 * Evidence for each decision (Req 18.4). Explicit `"unknown"` values render
 * verbatim (Req 18.6). When only the library entry is present (no artifact),
 * dimensions + confidence show and a placeholder stands in for evidence.
 */
function ClassificationPanel({
  libraryEntry,
  classification,
}: {
  libraryEntry: LibraryRepoEntry | null;
  classification: RepoClassificationArtifact | null;
}) {
  // Nothing to classify with: neither the joined entry nor the artifact.
  if (libraryEntry === null && classification === null) {
    return null;
  }

  const dimensions = resolveDimensions(libraryEntry, classification);
  const haveArtifact = classification !== null;

  return (
    <Section title="Classification">
      {dimensions.map((dimension) => (
        <div key={dimension.label} className="repo-detail__nested-item">
          <Row label={dimension.label}>
            <span>{dimension.value}</span>
            <ConfidenceBadge confidence={dimension.confidence} />
          </Row>
          <EvidenceList evidence={dimension.evidence} haveArtifact={haveArtifact} />
        </div>
      ))}
    </Section>
  );
}

/**
 * Render the grounding Evidence for one decision. With no artifact, a single
 * placeholder is shown; with an artifact but no evidence, the "no entries"
 * placeholder is shown; otherwise each evidence item lists its field refs,
 * matched rule id, recency, secret-like marker, and note.
 */
function EvidenceList({
  evidence,
  haveArtifact,
}: {
  evidence: ClassificationEvidence[];
  haveArtifact: boolean;
}) {
  if (!haveArtifact) {
    return (
      <Row label="Evidence">
        <PlaceholderText text="Evidence unavailable" />
      </Row>
    );
  }
  if (evidence.length === 0) {
    return (
      <Row label="Evidence">
        <PlaceholderText text="No evidence" />
      </Row>
    );
  }
  return (
    <Row label="Evidence">
      <ul className="repo-detail__list">
        {evidence.map((item, index) => (
          <li key={index} className="repo-detail__list-item">
            {item.field_refs.length > 0 && (
              <span>{item.field_refs.join(", ")}</span>
            )}
            {item.rule_id !== null && (
              <span className="repo-detail__muted">
                {item.field_refs.length > 0 ? " · " : ""}rule {item.rule_id}
              </span>
            )}
            {item.recency_days !== null && (
              <span className="repo-detail__muted"> · {item.recency_days}d</span>
            )}
            {item.secret_like && (
              <span className="repo-detail__secret-marker" title="secret-like">
                {" "}
                secret-like
              </span>
            )}
            {item.note !== null && item.note !== "" && (
              <span className="repo-detail__muted"> — {item.note}</span>
            )}
          </li>
        ))}
      </ul>
    </Row>
  );
}

/** Render a metric value, surfacing the explicit `"unavailable"` marker (Req 18.6). */
function MetricValueRow({
  label,
  value,
  format,
}: {
  label: string;
  value: number | string | Record<string, number> | null | undefined;
  format?: (value: number) => string;
}) {
  if (value === null || value === undefined) {
    return (
      <Row label={label}>
        <PlaceholderText text="unavailable" />
      </Row>
    );
  }
  if (value === "unavailable") {
    return (
      <Row label={label}>
        <PlaceholderText text="unavailable" />
      </Row>
    );
  }
  if (typeof value === "number") {
    return <Row label={label}>{format ? format(value) : String(value)}</Row>;
  }
  if (typeof value === "string") {
    return <Row label={label}>{value}</Row>;
  }
  // An object map (e.g. language composition / commit frequency): name -> count.
  const entries = Object.entries(value);
  return (
    <Row label={label}>
      {entries.length === 0 ? (
        <PlaceholderText text="No entries" />
      ) : (
        <ul className="repo-detail__list">
          {entries.map(([name, count], index) => (
            <li key={`${name}\u0000${index}`} className="repo-detail__list-item">
              <span>{name}</span>
              <span className="repo-detail__muted">
                {": "}
                {count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Row>
  );
}

/**
 * Activity-metrics panel sourced from the joined library entry's metrics
 * (Req 18.1). Renders the explicit `"unavailable"` markers (Req 18.6). Renders
 * nothing when there is no library entry or it carries no metrics.
 */
function ActivityMetricsPanel({
  libraryEntry,
}: {
  libraryEntry: LibraryRepoEntry | null;
}) {
  const metrics = libraryEntry?.metrics ?? null;
  if (metrics === null) {
    return null;
  }
  return (
    <Section title="Activity metrics">
      <MetricValueRow
        label="Size (MB)"
        value={metrics.sizeMb}
        format={(v) => String(v)}
      />
      <MetricValueRow
        label="Language composition"
        value={metrics.languageComposition}
      />
      <MetricValueRow label="Commit count" value={metrics.commitCount} />
      <MetricValueRow label="Commit frequency" value={metrics.commitFrequency} />
      <MetricValueRow label="Contributors" value={metrics.contributorCount} />
      <MetricValueRow label="Lines of code" value={metrics.linesOfCode} />
    </Section>
  );
}

/** Render one narrative text field, surfacing explicit markers (Req 18.6). */
function NarrativeFieldRow({ label, value }: { label: string; value: string }) {
  const degraded = value === "unavailable" || value === "unknown";
  return (
    <Row label={label}>
      {degraded ? <PlaceholderText text={value} /> : <span>{value}</span>}
    </Row>
  );
}

/**
 * Narrative panel: the `shortDescription` from the joined library entry plus the
 * full `purpose` / `interrelations` and the `narrative_succeeded` flag from the
 * fetched narrative artifact when present. When the narrative degraded
 * (`narrative_succeeded === false`) that is shown explicitly (Req 18.6). The
 * artifacts are already secret-safe; the fields are rendered as-is. Renders
 * nothing when neither source is present.
 */
function NarrativePanel({
  libraryEntry,
  narrative,
}: {
  libraryEntry: LibraryRepoEntry | null;
  narrative: NarrativeArtifact | null;
}) {
  const shortDescription = libraryEntry?.shortDescription ?? null;
  if (shortDescription === null && narrative === null) {
    return null;
  }
  return (
    <Section title="Narrative">
      {shortDescription !== null && (
        <NarrativeFieldRow label="Short description" value={shortDescription} />
      )}
      {narrative !== null && (
        <>
          {!narrative.narrative_succeeded && (
            <Row label="Status">
              <PlaceholderText text="Narrative unavailable (generation did not succeed)" />
            </Row>
          )}
          <NarrativeFieldRow label="Purpose" value={narrative.purpose} />
          <NarrativeFieldRow
            label="Interrelations"
            value={narrative.interrelations}
          />
        </>
      )}
    </Section>
  );
}
